import { Tooltip } from 'stories/components'

/** Mirrors the backend's PaymentKindTag (payment-kind.utils.ts). */
export interface PaymentKindTag {
  code: string
  label: string
}

export interface PaymentKind {
  kind: PaymentKindTag
  flags: PaymentKindTag[]
}

/**
 * Colour per code. Payment histories get long and messy, so the palette carries the meaning at a
 * glance: money that came in normally is blue, money that came back out or fell short is red or
 * amber, and everything descriptive stays a quiet gray so it never competes with a problem.
 */
const TONES: Record<string, string> = {
  NSF: 'bg-red-100 text-red-700 border-red-200',
  Reversal: 'bg-orange-100 text-orange-700 border-orange-200',
  Returned: 'bg-red-100 text-red-700 border-red-200',
  Reversed: 'bg-orange-100 text-orange-700 border-orange-200',
  PartialPmt: 'bg-amber-100 text-amber-800 border-amber-200',
  OverPmt: 'bg-sky-100 text-sky-800 border-sky-200',
  Payoff: 'bg-green-100 text-green-700 border-green-200',
  LateFee: 'bg-amber-100 text-amber-800 border-amber-200',
}

const KIND_TONE = 'bg-shade-blue/10 text-shade-blue border-shade-blue/20'
const FLAG_TONE = 'bg-gray-100 text-gray-600 border-gray-200'

const Badge = ({ tag, tone }: { tag: PaymentKindTag; tone: string }) => (
  <Tooltip message={tag.label}>
    <span className={`inline-block px-1.5 py-px rounded border text-[10px] leading-4 whitespace-nowrap ${tone}`}>
      {tag.code}
    </span>
  </Tooltip>
)

/**
 * The transaction-type badges for a payment history row: one for what the row is, then any number
 * qualifying it. Renders nothing when the row has no derived kind, so a cell that also prints the
 * gateway chip and the typed note stays clean.
 */
export const PaymentKindBadges = ({ kind }: { kind?: PaymentKind }) => {
  if (!kind) return null
  return (
    <div className="flex items-center gap-1">
      <Badge tag={kind.kind} tone={TONES[kind.kind.code] || KIND_TONE} />
      {kind.flags.map((flag) => (
        <Badge key={flag.code} tag={flag} tone={TONES[flag.code] || FLAG_TONE} />
      ))}
    </div>
  )
}
